/**
 * ReferralHistory - Displays a timeline of referrals with status indicators.
 * Shows referred user names, dates, and completion status.
 */

import type { Referral } from "../../hooks/useReferrals";

export interface ReferralHistoryProps {
  referrals: Referral[];
}

export function ReferralHistory({ referrals }: ReferralHistoryProps) {
  if (referrals.length === 0) {
    return (
      <div className="rounded-2xl border-[3px] border-dashed border-neutral-200 bg-neutral-50 p-8">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center">
            <UsersIcon className="w-8 h-8 text-neutral-400" />
          </div>
          <div>
            <p className="font-display font-bold text-body-lg text-neutral-700">
              No referrals yet
            </p>
            <p className="text-body-sm text-neutral-500 mt-1">
              Share your code with friends to see them here!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-[3px] border-neutral-200 bg-cloud overflow-hidden">
      <div className="divide-y divide-neutral-100">
        {referrals.map((referral, index) => (
          <div
            key={referral.id}
            className="
              flex items-center justify-between
              p-4 px-5
              transition-colors duration-200
              hover:bg-neutral-50
            "
            style={{
              animationDelay: `${index * 50}ms`,
            }}
          >
            {/* Left side: Status indicator + Name */}
            <div className="flex items-center gap-4">
              {/* Status dot with pulse animation for pending */}
              <div className="relative">
                <div
                  className={`
                    w-4 h-4 rounded-full
                    ${
                      referral.status === "completed"
                        ? "bg-meadow-500"
                        : referral.status === "pending"
                          ? "bg-sunshine-500"
                          : "bg-neutral-400"
                    }
                  `}
                />
                {referral.status === "pending" && (
                  <div className="absolute inset-0 w-4 h-4 rounded-full bg-sunshine-500 animate-ping opacity-50" />
                )}
              </div>

              {/* Name and date */}
              <div>
                <p className="font-display font-bold text-body-md text-neutral-800">
                  {referral.referredName}&apos;s family
                </p>
                <p className="text-body-sm text-neutral-500">
                  {formatDate(referral.referredAt)}
                </p>
              </div>
            </div>

            {/* Right side: Status badge or reward */}
            <div>
              {referral.status === "completed" && referral.rewardEarned ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-meadow-100 border-2 border-meadow-300 rounded-full">
                  <CheckIcon className="w-4 h-4 text-meadow-600" />
                  <span className="font-bold text-body-sm text-meadow-700">
                    +${(referral.rewardEarned.rewardValueCents / 100).toFixed(0)}
                  </span>
                </div>
              ) : referral.status === "pending" ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-sunshine-100 border-2 border-sunshine-300 rounded-full">
                  <ClockIcon className="w-4 h-4 text-sunshine-600" />
                  <span className="font-medium text-body-sm text-sunshine-700">
                    Pending
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 border-2 border-neutral-300 rounded-full">
                  <span className="font-medium text-body-sm text-neutral-500">
                    {referral.status}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper function to format dates in a friendly way
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
}

// Icons
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

export default ReferralHistory;
